import React, { FC, useState } from "react";

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import {
  AlertTriangleIcon,
  CircleMinus,
  CirclePlus,
} from "../../../components/icons";
import useQuota from "../../../hooks/use-quota";
import { setBannerMessage } from "../../../slices/pricing_info";
import { useDispatch } from "../../../store";
import {
  CustomDomain,
  ListCustomDomainsResponse,
  useDeleteCustomDomainMutation,
} from "../../../store/pomeriumApi";
import AddCustomDomainDialog from "./AddCustomDomainDialog";
import RetryCustomDomainButton from "./RetryCustomDomainButton";

export type CustomDomainsProps = {
  readonly: boolean;
  organizationId: string;
  clusterId: string;
  customDomains?: ListCustomDomainsResponse;
};

export const CustomDomains: FC<CustomDomainsProps> = ({
  readonly,
  organizationId,
  clusterId,
  customDomains,
}) => {
  const dispatch = useDispatch();
  const [deleteCustomDomain] = useDeleteCustomDomainMutation();
  const [showAdd, setShowAdd] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<null | CustomDomain>(
    null,
  );
  const { count: customDomainCount, limit: customDomainLimit } =
    useQuota("customDomains");

  function handleAdd() {
    if (customDomainCount >= customDomainLimit) {
      dispatch(
        setBannerMessage("To add another custom domain, upgrade your account."),
      );
    } else {
      setShowAdd(true);
    }
  }
  async function handleDelete(customDomainId: string) {
    await deleteCustomDomain({
      organizationId,
      customDomainId,
    });
  }

  const onConfirm = async () => {
    await handleDelete(domainToDelete?.id || "");
    onCancel();
  };

  const onCancel = () => {
    setDomainToDelete(null);
  };

  return (
    <>
      <AddCustomDomainDialog
        organizationId={organizationId}
        clusterId={clusterId}
        open={showAdd}
        onClose={() => setShowAdd(false)}
      />
      <ConfirmationDialog
        message={`Are you sure you would like to delete the custom domain, ${domainToDelete?.domainName}?`}
        open={!!domainToDelete}
        onCancel={onCancel}
        onConfirm={onConfirm}
        title="Delete custom domain?"
        variant="warning"
      />
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "transparent" }}>
            {!customDomains?.length && (
              <TableRow>
                <TableCell padding="none" width={32}>
                  <Tooltip title="Add Custom Domain">
                    <span>
                      <IconButton
                        size="small"
                        disabled={readonly}
                        onClick={handleAdd}
                        title="Add Custom Domain"
                      >
                        <CirclePlus color="primary" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell></TableCell>
                <TableCell padding="none" width={72} align="right"></TableCell>
              </TableRow>
            )}
            {!!customDomains?.length && (
              <TableRow>
                <TableCell padding="none" width={32}></TableCell>
                <TableCell>Domain Name</TableCell>
                <TableCell padding="none" width={72} align="right">
                  <Tooltip title="Add Custom Domain">
                    <span>
                      <IconButton
                        size="small"
                        disabled={readonly}
                        onClick={handleAdd}
                        title="Add Custom Domain"
                      >
                        <CirclePlus color="primary" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {customDomains?.length ? (
              customDomains?.map((customDomain) => (
                <TableRow key={customDomain.id}>
                  <TableCell padding="none">
                    {customDomain.lastError && (
                      <Tooltip title={customDomain.lastError}>
                        <span>
                          <IconButton size="small" sx={{ cursor: "default" }}>
                            <AlertTriangleIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{customDomain.domainName}</TableCell>
                  <TableCell padding="none" align="right">
                    <RetryCustomDomainButton
                      readonly={readonly}
                      organizationId={organizationId}
                      customDomain={customDomain}
                    />
                    <Tooltip title="Delete Custom Domain">
                      <span>
                        <IconButton
                          size="small"
                          disabled={readonly}
                          onClick={() => setDomainToDelete(customDomain)}
                        >
                          <CircleMinus />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>
                    <em>No Custom Domains Defined</em>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CustomDomains;
